import * as jsPdf from 'jspdf';
import { IOperatorRegionPrice } from 'src/Interfaces/AdminInterface';
import { IDispatchTruckTicket } from 'src/Interfaces/DispatchInterface';
import { ITest } from 'src/Interfaces/TestInterface';
import { IOperator, IWell, IWellCloseReview } from 'src/Interfaces/WellInterface';
import { ITenant } from 'src/Stores/Tenant/TenantStore';
import ConvertNulls from '../ConvertNulls';
import * as MUDDRAW from './MudReport/DrawReportMud';
import * as MM from './MudReport/SpecMilsMud';
import * as OPLDRAW from './OperatorPriceList/DrawOperatorPriceList';
import * as PISDRAW from './ProductInventorySnapshot/DrawReportProductInventory';
import * as TTDRAW from './TruckTicket/DrawTruckTicket';
import * as EDRAW from './VolumeAccountingReport/DrawReportExternal';
import * as VDRAW from './VolumeAccountingReport/DrawReportVolume';
import * as SDRAW from './VolumeAccountingSnapshotReport/DrawReportVolumeSnapshot';
import * as WCRDRAW from './WellCloseSnapshot/DrawWellCloseSnapshot';
import * as WSDRAW from './WellStoryReport/DrawReportWellStory';

const removeNulls = new ConvertNulls();

export default class PdfService {
    
    public static isTooBigForTourly = (test:ITest) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });

        mudReport.setFontSize(7);
        const tourlyArr = mudReport.splitTextToSize(test.remarks.tourlyTreatment, MM.TOURLY_WIDTH - 8);
        return (mudReport.getTextDimensions(tourlyArr).h > MM.TOURLY_HEIGHT - MM.SECTION_HEADER_HEIGHT - 1);
            
    }

    public static isTooBigForRecap = (test:ITest) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });

        mudReport.setFontSize(7);
        const recapArr = mudReport.splitTextToSize(test.remarks.mudReportRecap, MM.RECAP_WIDTH - 2);
        return (mudReport.getTextDimensions(recapArr).h > MM.RECAP_HEIGHT - MM.SECTION_HEADER_HEIGHT - 1);
            
    }

    public static createMudPdfBlob = async (test: ITest, well: IWell, logoUrl:string, draft?: boolean ) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });

        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        mudReport.setProperties({
            creator: "MudMetrix",
            title: "Mud Report " + test.testDate,
        });
        MUDDRAW.createMudReportPdfPage(test, well, logoBlob, mudReport, draft);

        return mudReport.output('blob');
    }

    public static createMudPdf = async (test: ITest, well: IWell, logoUrl:string, draft?: boolean ) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        mudReport.setProperties({
            creator: "MudMetrix",
            title: "Mud Report " + test.testDate,
        });
        MUDDRAW.createMudReportPdfPage(test, well, logoBlob, mudReport, draft);

        return mudReport.output('bloburi', {filename: 'Mud Report.pdf'});
    }

    public static createMudPdfAll = async (tests: ITest[], well: IWell, logoUrl:string, draft?: boolean) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        mudReport.setProperties({
            creator: "MudMetrix",
            title: "Mud Reports for Well " + well.wellName,
        });
        if( tests ) {
            tests.forEach((test, index) => {
                const noNullTest = removeNulls.recurseObject(test);
                MUDDRAW.createMudReportPdfPage(noNullTest, well, logoBlob, mudReport, draft);
                if (index !== tests.length -1) {
                    mudReport.addPage(); 
                }
            });
        }

        return mudReport.output('bloburi', {filename: 'Mud Report.pdf'});
    }

    public static createMudPdfForMultipleTests = async (testsToDisplay: ITest[], tests: ITest[], well: IWell, logoUrl: string) => {
        const mudReport = new jsPdf({
            format: 'legal',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        mudReport.setProperties({
            creator: "MudMetrix",
            title: "Mud Reports for Well " + well.wellName,
        });
        if( testsToDisplay ) {
            testsToDisplay.forEach((test, index) => {
                const noNullTest = removeNulls.recurseObject(test);
                MUDDRAW.createMudReportPdfPage(noNullTest, well, logoBlob, mudReport);
                if (index !== testsToDisplay.length -1) {
                    mudReport.addPage(); 
                }
            });
        }

        return mudReport.output('bloburi', {filename: 'Mud Report.pdf'});
    }

    public static createWellStoryPdf = (test: ITest, well: IWell, tenant: ITenant) => {
        const wellStoryReport = new jsPdf({
            format: 'letter',
            unit: 'mm',
        });

        wellStoryReport.setProperties({
            creator: "MudMetrix",
            title: "Well Story for Well " + well.wellName,
        });
        return WSDRAW.drawReportWellStory(test, well, tenant.tenantCompanyName, wellStoryReport)
            .then(() => {
                return wellStoryReport.output('bloburi', {filename: `WellStory-${well.wellName}.pdf`});
            });
    }

    public static createWellStoryBlob = (test: ITest, well: IWell, tenant: ITenant) => {
        const wellStoryReport = new jsPdf({
            format: 'letter',
            unit: 'mm',
        });

        wellStoryReport.setProperties({
            creator: "MudMetrix",
            title: "Well Story for Well " + well.wellName,
        });
        return WSDRAW.drawReportWellStory(test, well, tenant.tenantCompanyName, wellStoryReport)
            .then(() => {
                return wellStoryReport.output('blob');
            });
    }

    public static createVolumeSnapshotPdf = async (test: ITest, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Volume Accounting Report Well: " + well.wellName,
        });
        removeNulls.recurseObject(test);
        SDRAW.createVolumeSnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('dataurlstring', {filename: 'Volume Accounting Report.pdf'});
    }

    public static createVolumeSnapshotPdfMultiple = async (testsToDisplay: ITest[], tests: ITest, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Volume Accounting Report Well: " + well.wellName,
        });
        if (testsToDisplay)
        {
            testsToDisplay.forEach((test, index) => {
                const noNullTest = removeNulls.recurseObject(test);
                console.log(noNullTest, 'no null test');
                SDRAW.createVolumeSnapshotReport(noNullTest, well, logoBlob, volumeReport);
                if (index !== testsToDisplay.length - 1)
                {
                    volumeReport.addPage();
                }
            });
        }
        // removeNulls.recurseObject(test);
        // SDRAW.createVolumeSnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('dataurlstring', {filename: 'Volume Accounting Report.pdf'});
    }

    public static createVolumeSnapshotBlob = async (test: ITest, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Volume Accounting Report Well: " + well.wellName,
        });
        removeNulls.recurseObject(test);
        SDRAW.createVolumeSnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('blob');
    }

    public static createVolumePdf = async (tests: ITest[], well: IWell, wellCloseReview: IWellCloseReview | undefined, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Volume Accounting Report Well: " + well.wellName,
        });
        tests.forEach(value => value = removeNulls.recurseObject(value));
        VDRAW.createVolumeReport(tests, well, wellCloseReview, logoBlob, volumeReport);
        return volumeReport.output('dataurlstring', {filename: 'Volume Accounting Report.pdf'});
    }

    public static createVolumeBlob = async (tests: ITest[], well: IWell, wellCloseReview: IWellCloseReview | undefined, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Volume Accounting Report Well: " + well.wellName,
        });
        tests.forEach(value => value = removeNulls.recurseObject(value));
        VDRAW.createVolumeReport(tests, well, wellCloseReview, logoBlob, volumeReport);
        return volumeReport.output('blob');
    }

    public static createExternalPdf = async (tests: ITest[], well: IWell, logoUrl:string) => {
        const externalReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        externalReport.setProperties({
            creator: "MUDMAN",
            title: "External Storage Report Well: " + well.wellName,
        });
        tests.forEach(value => value = removeNulls.recurseObject(value));
        EDRAW.createExternalReport(tests, well, logoBlob, externalReport);
        return externalReport.output('dataurlstring', {filename: 'External Storage Report.pdf'});
    }

    public static createExternalBlob = async (tests: ITest[], well: IWell, logoUrl:string) => {
        const externalReport = new jsPdf({
            format: 'legal',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        externalReport.setProperties({
            creator: "MUDMAN",
            title: "External Storage Report Well: " + well.wellName,
        });
        tests.forEach(value => value = removeNulls.recurseObject(value));
        EDRAW.createExternalReport(tests, well, logoBlob, externalReport);
        return externalReport.output('blob');
    } 

    public static createProductInventorySnapshotPdf = async (test: ITest, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'portrait',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Product Inventory Report Well: " + well.wellName,
        });
        removeNulls.recurseObject(test);
        PISDRAW.createProductInventorySnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('dataurlstring', {filename: 'Volume Accounting Report.pdf'});
    }

    public static createProductInventorySnapshotPdfMultiple = async (testsToDisplay: ITest[], well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'portrait',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Product Inventory Report Well: " + well.wellName,
        });
        if (testsToDisplay)
        {
            testsToDisplay.forEach((test, index) => {
                const noNullTest = removeNulls.recurseObject(test);
                PISDRAW.createProductInventorySnapshotReport(noNullTest, well, logoBlob, volumeReport);
                if (index !== testsToDisplay.length - 1)
                {
                    volumeReport.addPage();
                }
            });
        }
        // removeNulls.recurseObject(test);
        // PISDRAW.createProductInventorySnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('dataurlstring', {filename: 'Volume Accounting Report.pdf'});
    }

    public static createProductInventorySnapshotBlob = async (test: ITest, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'legal',
            orientation: 'portrait',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Product Inventory Report Well: " + well.wellName,
        });
        removeNulls.recurseObject(test);
        PISDRAW.createProductInventorySnapshotReport(test, well, logoBlob, volumeReport);
        return volumeReport.output('blob');
    }

    public static createWellCloseReviewSnapshot = async (test: ITest, wellCloseReview: IWellCloseReview, well: IWell, logoUrl: string) => {
        const volumeReport = new jsPdf({
            format: 'letter',
            orientation: 'landscape',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(logoUrl);
        volumeReport.setProperties({
            creator: "MudMetrix",
            title: "Well Close Review Report Well: " + well.wellName,
        });
        removeNulls.recurseObject(test);
        WCRDRAW.createProductInventorySnapshotReport(test, well, wellCloseReview, logoBlob, volumeReport);
        return volumeReport.output('bloburi');
    }

    public static createOperatorRegionPriceList = async (tenant: ITenant, operator: IOperator, regionId: string, operatorRegionPrices: IOperatorRegionPrice[]) => {
        const operatorList = new jsPdf({
            format: 'letter',
            orientation: 'portrait',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(tenant.logoPathNameReport);
        OPLDRAW.drawOperatorPriceList(tenant, operator, regionId, operatorRegionPrices, logoBlob, operatorList);
        return operatorList.output('dataurlstring', {filename: 'Operator Region Price List.pdf'});
    }

    public static createDispatchTruckTicket = async (truck: IDispatchTruckTicket) => {
        const truckTicket = new jsPdf({
            format: 'letter',
            orientation: 'portrait',
            unit: 'mm',
        });
        const logoBlob = await PdfService.getLogoBlob(truck.tenant.logoPathNameReport);
        TTDRAW.createTruckTicket(truck, logoBlob, truckTicket);
        return truckTicket.output('dataurlstring', {filename: 'Truck Ticket.pdf'});
    }
    
    public static getLogoBlob(logoUrl: string) {
        const retImg = new Image();
        retImg.src = logoUrl;
        return retImg;
    }
}


// WEBPACK FOOTER //
// ./src/Services/Pdf/PdfService.ts